import { useEffect, useMemo, useState } from "react";
import { Check, MagnifyingGlass, Stack, X } from "@phosphor-icons/react";
import { SWATCHES } from "@/components/ColorChips";
import { photoHref } from "@/lib/api";
import { money } from "@/lib/format";

const KIND_LABEL = { slab: "In stock", remnant: "Remnant", catalog: "Catalogue" };
const KIND_TONE = {
  slab: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  remnant: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  catalog: "bg-zinc-700/40 text-zinc-300 border-zinc-600",
};

const hueOf = (s) => {
  let h = 0;
  for (const ch of String(s)) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
};
export const swatchStyle = (card) => {
  if (!card) return { backgroundImage: "linear-gradient(135deg, #3f3f46 0%, #18181b 100%)" };
  if (card.photo) return { backgroundImage: `url(${photoHref(card.photo)}&w=480)` };
  const a = SWATCHES[(card.primary || "").toLowerCase()] || `hsl(${hueOf(card.name)} 18% 38%)`;
  const b = SWATCHES[(card.accent || "").toLowerCase()] || `hsl(${(hueOf(card.name) + 40) % 360} 14% 22%)`;
  return { backgroundImage: `linear-gradient(135deg, ${a} 0%, ${b} 100%)` };
};

const sqftOf = (s) => ((Number(s.length_in) || 0) * (Number(s.width_in) || 0)) / 144;

// Builds one card per color+thickness from stock, one per remnant, one per catalogue color.
export const materialCards = (slabStock, remnants, colors, rates = {}) => {
  const stock = Object.values(
    (slabStock || []).reduce((acc, s) => {
      const key = `${s.slab_color}|${s.thickness || ""}`;
      const qty = Number(s.quantity) || 0;
      const first = (s.photos || []).find((p) => !p.is_deleted);
      const c = acc[key] || {
        value: `slab|${s.slab_color}|${s.stone_group || ""}|${s.thickness || ""}`,
        kind: "slab",
        name: s.slab_color,
        thickness: s.thickness || "",
        group: s.stone_group || "",
        material: s.material || "",
        finish: s.finish || "",
        supplier: s.supplier || "",
        primary: s.primary_color,
        accent: s.accent_color,
        photo: "",
        count: 0,
        sqft: 0,
        sizes: new Set(),
        locations: new Set(),
        price: 0,
      };
      c.count += qty;
      c.sqft += sqftOf(s) * Math.max(qty, 1);
      if (!c.photo && first) c.photo = first.storage_path;
      if (s.length_in && s.width_in) c.sizes.add(`${s.length_in}" × ${s.width_in}"`);
      if (s.location) c.locations.add(s.location);
      c.price = Number(s.sale_price_per_sqft) || c.price || Number(rates[s.stone_group]) || 0;
      acc[key] = c;
      return acc;
    }, {}),
  ).map((c) => ({
    ...c,
    sqft: Math.round(c.sqft),
    sizes: [...c.sizes],
    locations: [...c.locations],
  }));
  const rem = (remnants || []).map((r) => ({
    value: `remnant|${r.slab_color}||${r.thickness || ""}`,
    kind: "remnant",
    name: r.slab_color,
    thickness: r.thickness || "",
    group: r.stone_group || "",
    material: r.material || "",
    finish: r.finish || "",
    code: r.code || "",
    photo: ((r.photos || []).find((p) => !p.is_deleted) || {}).storage_path || "",
    count: 1,
    sqft: Math.round(Number(r.usable_sqft) || sqftOf(r)),
    sizes: r.length_in && r.width_in ? [`${r.length_in}" × ${r.width_in}"`] : [],
    locations: r.location ? [r.location] : [],
    price: Number(rates[r.stone_group]) || 0,
  }));
  const cat = (colors || [])
    .filter(
      (c) => !(slabStock || []).some((s) => s.slab_color === c.slab_color && s.thickness === c.thickness),
    )
    .map((c) => ({
      value: `catalog|${c.slab_color}||${c.thickness || ""}`,
      kind: "catalog",
      name: c.slab_color,
      thickness: c.thickness || "",
      group: "",
      material: c.material || "",
      finish: c.finish || "",
      supplier: c.supplier || "",
      count: 0,
      sqft: 0,
      sizes: [],
      locations: [],
      price: 0,
      unit_cost: Number(c.unit_cost) || 0,
    }));
  return [...stock, ...rem, ...cat];
};

const Card = ({ card, selected, onSelect, onUse, areaName }) => (
  <div
    data-testid={`material-card-${card.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
    className={`flex flex-col overflow-hidden rounded-2xl border text-left transition-all duration-200 ${
      selected
        ? "col-span-2 border-indigo-400/70 bg-zinc-900 shadow-[0_0_0_1px_rgba(129,140,248,0.4),0_16px_40px_-20px_rgba(99,102,241,0.8)]"
        : "border-zinc-800 bg-zinc-950/70 hover:-translate-y-0.5 hover:border-zinc-600"
    }`}
  >
    <button type="button" onClick={onSelect} className="text-left">
      <div className={`flex ${selected ? "flex-row" : "flex-col"}`}>
        <div
          className={`relative shrink-0 bg-cover bg-center ${selected ? "h-40 w-44" : "h-24 w-full"}`}
          style={swatchStyle(card)}
          data-testid="material-card-swatch"
        >
          <span
            className={`absolute left-2 top-2 rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest ${KIND_TONE[card.kind]}`}
          >
            {KIND_LABEL[card.kind]}
          </span>
          {selected && (
            <span className="absolute bottom-2 right-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-white">
              <Check size={13} weight="bold" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 p-3">
          <div className="truncate text-sm font-semibold text-zinc-100" data-testid="material-card-name">
            {card.name}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-zinc-400">
            {card.thickness && <span>{card.thickness}</span>}
            {card.group && <span>· {card.group}</span>}
            {card.material && <span>· {card.material}</span>}
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5 font-mono-data text-[10px]">
            {card.kind === "slab" && (
              <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-zinc-300">
                {card.count} slab{card.count === 1 ? "" : "s"} · {card.sqft} ft²
              </span>
            )}
            {card.kind === "remnant" && (
              <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-zinc-300">
                {card.code} · {card.sqft} ft² usable
              </span>
            )}
            {card.price > 0 && (
              <span className="rounded-md bg-indigo-500/15 px-1.5 py-0.5 text-indigo-200">
                {money(card.price)}/ft²
              </span>
            )}
            {card.kind === "catalog" && card.unit_cost > 0 && (
              <span className="rounded-md bg-zinc-800 px-1.5 py-0.5 text-zinc-400">
                cost {money(card.unit_cost)}/ft²
              </span>
            )}
          </div>
          {selected && (
            <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-[11px]" data-testid="material-card-details">
              {[
                ["Finish", card.finish],
                ["Supplier", card.supplier],
                ["Sizes", card.sizes.join(", ")],
                ["Location", card.locations.join(", ")],
                ["Price group", card.group],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div key={k} className="min-w-0">
                    <dt className="text-[9px] uppercase tracking-widest text-zinc-500">{k}</dt>
                    <dd className="truncate text-zinc-200">{v}</dd>
                  </div>
                ))}
            </dl>
          )}
        </div>
      </div>
    </button>
    {selected && (
      <div className="flex items-center justify-between gap-3 border-t border-zinc-800 px-3 py-2.5">
        <span className="text-[11px] text-zinc-500">
          {card.kind === "catalog"
            ? "Not in stock — will be ordered in."
            : card.kind === "remnant"
              ? "Uses this remnant for the area."
              : "Priced from your group rate."}
        </span>
        <button
          type="button"
          data-testid="material-use"
          onClick={onUse}
          className="flex h-9 items-center gap-2 rounded-full bg-indigo-500 px-4 text-[10px] font-bold uppercase tracking-widest text-white transition-colors duration-150 hover:bg-indigo-400"
        >
          <Check size={12} weight="bold" /> Use for {areaName || "this area"}
        </button>
      </div>
    )}
  </div>
);

export const MaterialDrawer = ({
  open,
  onClose,
  cards,
  value,
  areaName,
  onPick,
}) => {
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const currentColor = String(value || "").split("|").length > 1 ? value.split("|")[1] : value;
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (open) setSelected(cards.find((c) => c.name === currentColor)?.value || null);
  }, [open, cards, currentColor]);
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [open, onClose]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return cards.filter(
      (c) =>
        (kind === "all" || c.kind === kind) &&
        (!t || `${c.name} ${c.group} ${c.material} ${c.thickness} ${c.code || ""}`.toLowerCase().includes(t)),
    );
  }, [cards, q, kind]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end bg-zinc-950/70 backdrop-blur-sm"
      onClick={onClose}
      data-testid="material-drawer"
      role="dialog"
      aria-modal="true"
    >
      <aside
        className="flex h-full w-[min(44rem,96vw)] flex-col border-l border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-zinc-800 px-5 py-4">
          <Stack size={18} weight="bold" className="text-indigo-300" />
          <div>
            <div className="text-sm font-bold text-zinc-50">Material</div>
            <div className="text-[11px] text-zinc-500">
              {areaName ? `for ${areaName} · ` : ""}
              {cards.length} options
            </div>
          </div>
          <button
            type="button"
            data-testid="material-drawer-close"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto flex h-9 w-9 items-center justify-center rounded-lg text-zinc-400 transition-colors duration-150 hover:bg-zinc-800 hover:text-white"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2 border-b border-zinc-800 px-5 py-3">
          <label className="flex h-10 flex-1 min-w-[12rem] items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-900 px-3">
            <MagnifyingGlass size={14} className="text-zinc-500" />
            <input
              data-testid="material-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search colors, groups, remnant codes…"
              className="w-full bg-transparent text-sm text-zinc-100 outline-none placeholder:text-zinc-600"
            />
          </label>
          <div className="flex gap-1 rounded-lg border border-zinc-800 bg-zinc-900 p-1">
            {[
              ["all", "All"],
              ["slab", "In stock"],
              ["remnant", "Remnants"],
              ["catalog", "Catalogue"],
            ].map(([k, label]) => (
              <button
                key={k}
                type="button"
                data-testid={`material-filter-${k}`}
                onClick={() => setKind(k)}
                className={`h-8 rounded-md px-3 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                  kind === k ? "bg-indigo-500 text-white" : "text-zinc-400 hover:text-zinc-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {shown.length === 0 ? (
            <p className="py-16 text-center text-sm text-zinc-500" data-testid="material-empty">
              Nothing matches — try another search.
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="material-grid">
              {shown.map((c) => (
                <Card
                  key={c.value}
                  card={c}
                  areaName={areaName}
                  selected={selected === c.value}
                  onSelect={() => setSelected(selected === c.value ? null : c.value)}
                  onUse={() => {
                    onPick(c.value);
                    onClose();
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};

export default MaterialDrawer;
